import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { CloudUpload, FolderFill, FileEarmarkText } from 'react-bootstrap-icons';
import DashboardLayout from '../components/DashboardLayout';
import UploadFileForm from '../components/UploadFileForm';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { getActiveCompanyId } from '../utils/companyContext';
import '../assets/styles/files.css';

const Files = () => {
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileNames, setFileNames] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [documentGroups, setDocumentGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());

  const actionButtonGroupClass = [
    'd-flex',
    'flex-column',
    'flex-md-row',
    'justify-content-md-end',
    'align-items-stretch',
    'align-items-md-center',
    'gap-3',
    'mb-4',
  ].join(' ');

  const normalizeDocumentGroups = useCallback((data = []) => {
    if (!Array.isArray(data)) {
      return [];
    }

    if (data.length > 0 && Array.isArray(data[0].documents)) {
      return data.map((group) => ({
        ...group,
        documents: Array.isArray(group.documents) ? group.documents : [],
      }));
    }

    const grouped = data.reduce((groups, document) => {
      const formName = document?.fileName?.name || 'Uncategorized';
      const fileNameId = document?.fileName?.id ?? null;
      const key = fileNameId ?? formName;

      if (!groups[key]) {
        groups[key] = { formName, fileNameId, documents: [] };
      }

      groups[key].documents.push(document);
      return groups;
    }, {});

    return Object.values(grouped);
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      if (!activeCompanyId) {
        setDocumentGroups([]);
        setError('Select a company to view files.');
        return;
      }

      const params = new URLSearchParams();
      params.set('companyId', activeCompanyId);

      const { data } = await apiRequest(`/api/admin/files/documents?${params.toString()}`);
      setDocumentGroups(normalizeDocumentGroups(Array.isArray(data) ? data : []));
    } catch (err) {
      setError(err.message || 'Failed to load files.');
      setDocumentGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeCompanyId, normalizeDocumentGroups]);

  const loadMetadata = useCallback(async () => {
    try {
      if (!activeCompanyId) {
        setFileNames([]);
        setOutlets([]);
        return;
      }

      const params = new URLSearchParams();
      params.set('companyId', activeCompanyId);

      const [fileNameResponse, outletResponse] = await Promise.all([
        apiRequest(`/api/admin/files/names?${params.toString()}`),
        apiRequest(`/api/admin/settings/outlets?${params.toString()}`),
      ]);

      setFileNames(Array.isArray(fileNameResponse.data) ? fileNameResponse.data : []);
      setOutlets(Array.isArray(outletResponse.data) ? outletResponse.data : []);
    } catch (metadataError) {
      console.error('Failed to load file metadata:', metadataError);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
      setOutlets([]);
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  const handleDocumentUploaded = useCallback(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const sortedFileNames = useMemo(() => {
    return [...fileNames].sort((a, b) => a.fileName.localeCompare(b.fileName));
  }, [fileNames]);

  const sortedOutlets = useMemo(() => {
    return [...outlets].sort((a, b) => a.branchName.localeCompare(b.branchName));
  }, [outlets]);
  const folderGroups = useMemo(() => {
    return [...documentGroups]
      .map((group) => ({
        ...group,
        documents: [...group.documents].sort(
          (first, second) => new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime(),
        ),
      }))
      .sort((first, second) => first.formName.localeCompare(second.formName));
  }, [documentGroups]);

  const folderCards = useMemo(() => {
    const groupMap = new Map();

    folderGroups.forEach((group) => {
      const folderId = group.fileNameId ?? group.formName;
      groupMap.set(folderId, {
        formName: group.formName,
        count: group.documents.length,
        fileNameId: group.fileNameId,
      });
    });

    const baseFolders = sortedFileNames.map((file) => ({
      id: file.id,
      formName: file.fileName,
      count: groupMap.get(file.id)?.count ?? 0,
    }));

    const extraFolders = folderGroups
      .filter((group) => !group.fileNameId)
      .map((group) => ({
        id: group.formName,
        formName: group.formName,
        count: group.documents.length,
      }))
      .filter((folder) => !baseFolders.some((base) => base.formName === folder.formName));

    return [...baseFolders, ...extraFolders];
  }, [folderGroups, sortedFileNames]);

  const handleOpenFolder = useCallback(
    (folderId, formName) => {
      if (!folderId && !formName) {
        return;
      }

      navigate(`/files/${encodeURIComponent(folderId || formName)}`, {
        state: { formName },
      });
    },
    [navigate],
  );

  return (
    <DashboardLayout>
      <div className="files-page container-fluid py-4">
        <div className={actionButtonGroupClass}>
          <button
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => setShowUploadModal(true)}
          >
            <CloudUpload className="me-2" />
            Upload New File
          </button>
          <button
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/files/names')}
          >
            File Name Create/Edit
          </button>
          <button
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate('/resume')}
          >
            Resume
          </button>
        </div>

        <div className="card files-table-card shadow-sm">
          <div className="card-body">
            {isLoading ? (
              <div className="py-5 text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : error ? (
              <div className="py-5 px-3">
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              </div>
            ) : folderCards.length === 0 ? (
              <div className="files-empty-state text-center py-5">
                <div className="files-empty-icon d-inline-flex align-items-center justify-content-center mb-3">
                  <FileEarmarkText size={28} />
                </div>
                <p className="mb-1 fw-semibold">No documents found.</p>
                <small className="text-muted">Try adjusting filters or upload a new file.</small>
              </div>
            ) : (
              <div className="files-folder-grid">
                {folderCards.map((folder) => (
                  <button
                    key={folder.id}
                    type="button"
                    className="files-folder-card open text-start"
                    onDoubleClick={() => handleOpenFolder(folder.id, folder.formName)}
                  >
                    <div className="files-folder-header">
                      <div className="files-folder-title">
                        <div className="files-folder-icon">
                          <FolderFill size={26} />
                        </div>
                        <div>
                          <p className="mb-0 fw-semibold text-capitalize">{folder.formName}</p>
                          <small className="text-muted">
                            {folder.count} {folder.count === 1 ? 'file' : 'files'}
                          </small>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <UploadFileForm
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          fileNames={fileNames}
          outlets={outlets}
          onUploaded={handleDocumentUploaded}
        />
      </div>
    </DashboardLayout>
  );
};

export default Files;